<template>
    <VForm ref="form" v-model="formIsValid" :disabled="form.disabled">
        <VRow>
            <VCol xlg="4" lg="5" md="6" sm="12">
                <SrHeadline class="mb-6" level="3" weight="bold"> URL Assets </SrHeadline>
                <SrURLInput
                    v-model="viewModel.advertiserUrl"
                    :rules="viewModel.rules.url"
                    class="advertiser-url"
                    label="Advertiser URL"
                    maxlength="2048"
                    required
                    secure-url
                />
                <VastUrlInput v-model="viewModel.vastUrl" />
                <SrHeadline class="mb-6" level="3" weight="bold"> Attributes Input Method </SrHeadline>
                <VRadioGroup v-model="viewModel.isVastUrlParsed" row>
                    <SrRadioButton :value="true" label="Auto" />
                    <SrRadioButton :value="false" label="Manual" />
                </VRadioGroup>
                <SrLabel
                    v-if="viewModel.isVastUrlParsed"
                    class="ma-1"
                    color="blue"
                    text="Parses some VAST properties from VAST URL secure content."
                />
                <div v-else>
                    <VastVersion v-model="viewModel.vastVersion" />
                    <SrInput
                        v-model="viewModel.duration"
                        :rules="viewModel.rules.duration"
                        class="vast-duration"
                        label="Video duration [s]"
                        type="number"
                        min="1"
                        step="1"
                        maxlength="10"
                        required
                    />
                    <VideoVastMimeTypeSelect v-model="viewModel.mimeTypes" />
                    <VastApiFrameworksSelect v-model="viewModel.vastApiFrameworks" />
                </div>
            </VCol>
            <VCol v-if="isVideoUploadEnabled" xlg="4" lg="5" md="6" sm="12" offset-lg="1">
                <SrHeadline level="3" weight="bold" class="mb-6"> Video upload </SrHeadline>
                <div class="click-url-row">
                    <ClickUrl
                        v-model="viewModel.clickUrl"
                        class="click-url-input"
                        :is-disabled="shouldDisableClickUrl"
                        :is-required="false"
                        @update:error="onClickUrlError"
                    />
                    <SrTooltip class="click-url-icon" left>
                        <template #activator="{ on }">
                            <div class="info-icon" v-on="on">i</div>
                        </template>
                        <div class="brand-blue--text">Once a video is uploaded, the click URL cannot be changed anymore.</div>
                    </SrTooltip>
                </div>
                <VideoDropzone v-if="shouldShowVideoDropzone" :click-url="viewModel.clickUrl" @update:vastUrl="updateVastUrl" />
            </VCol>
        </VRow>
    </VForm>
</template>

<script>
import { SrHeadline, SrInput, SrLabel, SrRadioButton, SrTooltip, SrURLInput } from '@ads/design-system';
import FormBasics from '@/components/domain/creatives/FormBasics';
import VideoAssetsViewModel from '@/components/domain/creatives/video/VideoAssetsViewModel';
import VastUrlInput from '@/components/domain/vast/VastUrlInput';
import VastVersion from '@/components/domain/vast/VastVersion';
import VastApiFrameworksSelect from '@/components/domain/vast/VastApiFrameworksSelect';
import VideoVastMimeTypeSelect from '@/components/domain/vast/VideoVastMimeTypeSelect';
import VideoDropzone from '@/components/domain/creatives/video/video-dropzone/VideoDropzone';
import ClickUrl from '@/components/domain/creatives/shared/basics/ClickUrl';

export default {
    name: 'VideoAssets',
    components: {
        ClickUrl,
        VideoDropzone,
        VideoVastMimeTypeSelect,
        VastVersion,
        VastApiFrameworksSelect,
        VastUrlInput,
        SrHeadline,
        SrURLInput,
        SrRadioButton,
        SrInput,
        SrLabel,
        SrTooltip,
    },
    mixins: [FormBasics],
    data() {
        return {
            viewModel: new VideoAssetsViewModel(this.value),
            isVideoUploadEnabled: false,
            isClickUrlValid: true,
            shouldDisableClickUrl: false,
        };
    },
    computed: {
        shouldShowVideoDropzone() {
            return this.viewModel.hasClickUrl() && this.isClickUrlValid;
        },
    },
    watch: {
        value(creative) {
            this.viewModel.reInitialize(creative);
        },
    },
    async created() {
        this.isVideoUploadEnabled = await this.$root.user.hasFeature('video-upload');
    },
    methods: {
        updateVastUrl(vastUrl) {
            if (vastUrl) {
                this.viewModel.vastUrl = vastUrl;
                this.shouldDisableClickUrl = true;
                return;
            }
            this.viewModel.vastUrl = '';
            this.shouldDisableClickUrl = false;
        },
        onClickUrlError(hasError) {
            this.isClickUrlValid = !hasError;
        },
    },
};
</script>
<style scoped>
.click-url-row {
    display: flex;
    align-items: center;
}
.click-url-input {
    flex: 1;
}
.click-url-icon {
    flex: 0;
    margin-left: 16px;
    margin-bottom: 24px;
}

.info-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    font-weight: 500;
    border-radius: 50%;
    border: 2px solid;
    height: 24px;
    width: 24px;
}
</style>
